'use client';
import BrandLogo from '@/views/commonComponent/brand/BrandLogo';
import { Suspense, useState, useEffect } from 'react';
import { auth } from '../../../fire-base/FirebaseAuth';
import type { FormInstance } from 'antd';
import { Button, Checkbox, Form, Input } from 'antd';
import { Container, Row, Col, Modal } from 'react-bootstrap';
import { emailPattern, passwordPattern, requiredTextField } from '../../../utils/FormValidations';
import Link from 'next/link';
import { appAction } from '@/store/store';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  GoogleAuthProvider,
  OAuthProvider,
  createUserWithEmailAndPassword,
  signInWithPopup,
} from 'firebase/auth';
import { TNCs, Description, UserAggrement } from './termAndConditions';

import '../style.scss';
import { user } from '@/model/authModel/AuthModel';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { toaster } from '@/utils/toast';
import TermAndUserAggrementsModal from './TermAndCondition';

const SubmitButton = ({ form }: { form: FormInstance }) => {
  const [submittable, setSubmittable] = useState(false);

  // Watch all values
  const values = Form.useWatch([], form);

  useEffect(() => {
    form.validateFields({ validateOnly: true }).then(
      () => {
        setSubmittable(true);
      },
      () => {
        setSubmittable(false);
      }
    );
  }, [values, form]);

  return (
    <Button className="primaryButton" type="primary" htmlType="submit" disabled={!submittable}>
      Sign Up
    </Button>
  );
};

const SignUp = (props: any) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const searchParam = searchParams.get('data');
  const [form] = Form.useForm();
  const [userAggrementModal, setUserAggrementModal] = useState(false);
  const [termAndServiceCheck, setTermAndServiceCheck] = useState(false);
  const authCreate = appAction((actions: any) => actions.AuthModel.authCreate);
  const setUser = appAction((action) => action.AuthModel.setUserCreate);
  const handleGoogleLogin = () => {
    if (!termAndServiceCheck) {
      toaster('error', 'Please agree to terms and conditions');
      return;
    }
    const provider = new GoogleAuthProvider();
    signInWithPopup(auth, provider).then((res) => {
      const newObj: user = {
        first_name: res.user.displayName?.split(' ')?.slice(0, -1)?.join(' ') || '',
        last_name: res.user.displayName?.split(' ').slice(-1).join(' ') || '',
        email: res.user.email || '',
        type: searchParam ? searchParam : '',
        sso: true,
      };
      authCreate(newObj).then((r: any) => {
        if (r != 'error') {
          router.push('/dashboard');
        }
      });
    });
  };
  const handleMicroSoftLogin = () => {
    if (!termAndServiceCheck) {
      toaster('error', 'Please agree to terms and conditions');
      return;
    }
    const provider = new OAuthProvider('microsoft.com');
    signInWithPopup(auth, provider).then((res) => {
      const newObj: user = {
        first_name: res.user.displayName?.split(' ')?.slice(0, -1)?.join(' ') || '',
        last_name: res.user.displayName?.split(' ').slice(-1).join(' ') || '',
        email: res.user.email || '',
        type: searchParam ? searchParam : '',
        sso: true,
      };
      authCreate(newObj);
      router.push('/dashboard');
    });
  };

  const onFinish = async (values: any) => {
    if (!termAndServiceCheck) {
      toaster('error', 'Please agree to term and services!');
      return;
    }
    const newObj: user = {
      first_name: values?.first_name,
      last_name: values?.last_name,
      email: values?.email,
      type: searchParam ? searchParam : '',
      sso: false,
    };

    // Order matters: create Firebase account FIRST, then the backend
    // record. The reverse order would leave us with a backend-without-
    // Firebase lockout if Firebase later rejects (password policy,
    // blocked domain, account already exists, network blip): authCreate
    // would already have persisted the user, and any retry would short-
    // circuit on "Email already exists" and never re-attempt Firebase.
    // Mirrors the SSO flows (Google / Microsoft), which already do
    // signInWithPopup before authCreate.
    try {
      await createUserWithEmailAndPassword(auth, values?.email, values?.password);
    } catch {
      toaster('error', 'Failed to create account. Please try again.');
      return;
    }

    authCreate(newObj)
      .then((res: any) => {
        if (res === 'success') {
          router.push('/auth/email-verify');
        }
        // If authCreate fails, it has already toasted; the user has a
        // Firebase account but no backend record. They can sign in once
        // backend signup recovers (or admin intervenes) -- this is the
        // less-bad failure mode vs. backend-without-Firebase.
      })
      .catch(() => {
        // authCreate already toasts on failure; defensive net only.
      });
  };

  const onFinishFailed = (errorInfo: any) => {};

  return (
    <>
      <div className="auth-main-div">
        <Container fluid>
          <Row>
            <Col xs={12} md={6}>
              <div className="authLeftside">
                <Link href="/">
                  <BrandLogo size="sm" href="/" className="loginLogo" />
                </Link>
                <h5 className="loginHeading">Register Now</h5>
                <p className="loginSubHeading" style={{ marginLeft: '0px' }}>
                  Let’s get you signed up, just a few simple details first.
                </p>
                <Form
                  name="basic"
                  initialValues={{ remember: true }}
                  onFinish={onFinish}
                  onFinishFailed={onFinishFailed}
                  autoComplete="off"
                  layout="vertical"
                  form={form}
                >
                  <Row>
                    <Col lg={6}>
                      <Button className="sso-button" onClick={handleGoogleLogin}>
                        <img src="/auth/google.svg" alt="google.svg" />
                        Sign up with Google
                      </Button>
                    </Col>
                    <Col lg={6}>
                      <Button className="sso-button" onClick={handleMicroSoftLogin}>
                        <img src="/auth/microsoft.svg" alt="microsoft.svg" />
                        Sign up with Microsoft
                      </Button>
                    </Col>
                  </Row>
                  <p className="signup-or">OR</p>
                  <Row>
                    <Col xs={12} lg={6}>
                      <Form.Item
                        name="first_name"
                        label="First Name"
                        rules={[
                          {
                            required: true,
                            pattern: requiredTextField.value,
                            message: requiredTextField.message,
                          },
                        ]}
                      >
                        <Input placeholder="Enter your first name" type="a" />
                      </Form.Item>
                    </Col>
                    <Col>
                      <Form.Item
                        name="last_name"
                        label="Last Name"
                        rules={[
                          {
                            required: true,
                            pattern: requiredTextField.value,
                            message: requiredTextField.message,
                          },
                        ]}
                      >
                        <Input placeholder="Enter your last name" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    name="email"
                    label="Email Address"
                    rules={[
                      {
                        required: true,
                        pattern: emailPattern.value,
                        message: emailPattern.message,
                      },
                    ]}
                  >
                    <Input placeholder="Enter email address" />
                  </Form.Item>

                  <Form.Item
                    name="password"
                    label="Password"
                    rules={[
                      {
                        required: true,
                        pattern: passwordPattern.value,
                        message: passwordPattern.message,
                      },
                    ]}
                  >
                    <Input.Password
                      onPaste={(e) => {
                        e.preventDefault();
                        return false;
                      }}
                      onCopy={(e) => {
                        e.preventDefault();
                        return false;
                      }}
                      placeholder="Password"
                    />
                  </Form.Item>
                  <Form.Item
                    name="confirm_password"
                    label="Confirm password"
                    dependencies={['password']}
                    hasFeedback
                    rules={[
                      {
                        required: true,
                        message: 'Please confirm your password!',
                      },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('password') === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error('The new password that you entered do not match!')
                          );
                        },
                      }),
                    ]}
                  >
                    <Input.Password
                      onPaste={(e) => {
                        e.preventDefault();
                        return false;
                      }}
                      placeholder="Confirm Password"
                    />
                  </Form.Item>

                  <Form.Item
                    name="remember"
                    rules={[
                      {
                        required: true,
                        message: 'Please agree to all the terms & conditions',
                      },
                    ]}
                  >
                    <Checkbox
                      checked={termAndServiceCheck}
                      onChange={(e: CheckboxChangeEvent) =>
                        setTermAndServiceCheck(e.target.checked)
                      }
                    >
                      Yes, I understand and agree to the
                    </Checkbox>
                    <span style={{ fontSize: '13px', cursor: 'pointer', color: 'primaryColor' }}>
                      <span
                        style={{ padding: '0px', margin: '0px', fontWeight: 'bold' }}
                        onClick={() => setUserAggrementModal(true)}
                      >
                        Terms of Services
                      </span>
                      , including the{' '}
                      <span
                        style={{ padding: '0px', margin: '0px', fontWeight: 'bold' }}
                        onClick={() => setUserAggrementModal(true)}
                      >
                        User agreement
                      </span>{' '}
                      and Privacy Policy.
                    </span>
                  </Form.Item>

                  <Form.Item>
                    <SubmitButton form={form} />
                  </Form.Item>
                </Form>
                <div className="back-to-login">
                  <p>
                    Already have an account? <Link href="/auth/login">Login</Link>
                  </p>
                </div>
              </div>
            </Col>
            <Col xs={12} md={6} className="authBgImage">
              <div className="authRightside">
                <div className="contentPart">
                  <p>
                    “This is an ever changing industry with new technology and processes. But we
                    still needed talented people to get the work done. We welcome you to our
                    platform and the community.”
                  </p>
                  <h5>Andrew J. Evans</h5>
                  <h6>Founder</h6>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
      <TermAndUserAggrementsModal
        userAggrementModalOpen={userAggrementModal}
        userAggrementModalClose={(val: any) => {
          if (val === 'agree') {
            setTermAndServiceCheck(true);
          } else if (val === 'disagree') {
            setTermAndServiceCheck(false);
          }
          setUserAggrementModal(false);
        }}
        footer={true}
      />
    </>
  );
};
const SignUpPage = (props: any) => (
  <Suspense>
    <SignUp {...props} />
  </Suspense>
);

export default SignUpPage;
